import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import TeethSelector from './TeethSelector';
import { Disclosure } from '@headlessui/react';
import { ChevronUpIcon } from '@heroicons/react/20/solid';
import { apiClient } from '../../services/apiClient';
import jsPDF from 'jspdf';
import medidentVsCcBg1 from '../../assets/medident-vs-cc-fr-1.png';
import medidentVsCcBg2 from '../../assets/medident-vs-cc-fr-2.png';
import colors from '../../config/colors';
import { normalizeTeethListFields, parseTeethList } from '../../utils/teethList';
import EuropeanDateInput from '../common/EuropeanDateInput';

export const medidentVsCcDefaultFormData = {
  'no-assure': '',
  'nom-prenom': '',
  adresse: '',
  'date-naissance': '',
  'medecin-dentiste': '',
  telephone: '',
  'date-1ere-consultation': '',
  'motif-consultation': '',
  hygiene: '',
  fumeur: 'non',
  'cas-lamal-opas': 'non',
  'cas-accident': 'non',
  'prestations-complementaires': 'non',
  'missing-chart-teeth': '',
  'missing-teeth': '',
  'defective-teeth': '',
  'repaired-teeth': '',
  'periodontal-teeth': '',
  'prosthetic-description': '',
  'mesures-diagnostiques': '',
  'mesures-therapeutiques': '',
  'soins-definitifs': '',
  'annexe-plan-traitement': false,
  'annexe-devis-dentiste': true,
  'annexe-devis-laboratoire': false,
  'annexe-radiographie': false,
  'annexe-autre': false,
  'annexe-autre-texte': '',
  'lieu-date': '',
};

const MEDIDENT_IMG_W = 1191;
const MEDIDENT_IMG_H = 1684;

const HYGIENE_OPTIONS = ['bonne', 'moyenne', 'mauvaise'];

// Checkbox centers from AcroForm (image px)
const toothChartCenters = {
  55: [300.25, 1192.55], 54: [330.35, 1192.55], 53: [360.5, 1192.55], 52: [390.6, 1192.55], 51: [420.75, 1192.55],
  61: [450.4, 1192.65], 62: [480.5, 1192.65], 63: [510.65, 1192.65], 64: [540.8, 1192.65], 65: [570.9, 1192.65],
  18: [632.85, 1192.7], 17: [662.95, 1192.7], 16: [693.1, 1192.7], 15: [723.2, 1192.7], 14: [753.35, 1192.7],
  13: [781.8, 1192.7], 12: [811.9, 1192.7], 11: [840.7, 1192.7],
  21: [870.95, 1192.7], 22: [901.1, 1192.7], 23: [931.3, 1192.7], 24: [961.4, 1192.7], 25: [991.55, 1192.7],
  26: [1021.7, 1192.7], 27: [1051.25, 1192.7], 28: [1081.25, 1192.65],
  85: [300.25, 1215.2], 84: [330.3, 1215.2], 83: [360.45, 1215.2], 82: [390.65, 1215.2], 81: [420.65, 1215.2],
  71: [450.4, 1215.3], 72: [480.45, 1215.3], 73: [510.6, 1215.3], 74: [540.8, 1215.3], 75: [570.8, 1215.3],
  48: [632.85, 1215.35], 47: [662.9, 1215.35], 46: [693.05, 1215.35], 45: [723.25, 1215.35], 44: [753.25, 1215.35],
  43: [781.8, 1215.3], 42: [811.85, 1215.3], 41: [840.65, 1215.3],
  31: [870.95, 1215.3], 32: [901.0, 1215.3], 33: [931.3, 1215.3], 34: [961.35, 1215.3], 35: [991.5, 1215.3],
  36: [1021.7, 1215.3], 37: [1051.1, 1215.3], 38: [1081.3, 1215.3],
};

const statusOuiNon = {
  'missing-teeth': { oui: [504.1, 1276.9], non: [541.15, 1276.95], upperY: 1278, lowerY: 1299 },
  'defective-teeth': { oui: [504.5, 1320.6], non: [541.05, 1320.6], upperY: 1322, lowerY: 1343 },
  'repaired-teeth': { oui: [504.6, 1364.4], non: [541.15, 1364.25], upperY: 1365, lowerY: 1387 },
  'periodontal-teeth': { oui: [504.6, 1408.05], non: [541.05, 1408], upperY: 1409, lowerY: 1430 },
};

// Status tables: center bar is left of the odontogram cross — shift so 11|21 (and 41|31) straddle it.
const ODO_CENTER_X = (toothChartCenters[11][0] + toothChartCenters[21][0]) / 2;
const STATUS_GRID_CENTER_X = 823.3;
const STATUS_GRID_X_OFFSET = STATUS_GRID_CENTER_X - ODO_CENTER_X;

// Deciduous overlay permanent columns in the 4 status grids (same X as 1x/2x/3x/4x).
const DECIDUOUS_TO_PERMANENT = {
  55: 15, 54: 14, 53: 13, 52: 12, 51: 11,
  61: 21, 62: 22, 63: 23, 64: 24, 65: 25,
  85: 45, 84: 44, 83: 43, 82: 42, 81: 41,
  71: 31, 72: 32, 73: 33, 74: 34, 75: 35,
};

const hygieneMarks = {
  bonne: [480.0, 807.75],
  moyenne: [658.6, 807.7],
  mauvaise: [836.9, 807.7],
};

const annexMarks = {
  'annexe-plan-traitement': [223.15, 1482.6],
  'annexe-devis-dentiste': [223.25, 1507.25],
  'annexe-devis-laboratoire': [223.05, 1534.05],
  'annexe-radiographie': [223.1, 1560.75],
  'annexe-autre': [223.15, 1586.95],
};

function debounce(fn, delay) {
  let timer = null;
  return (...args) => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delay);
  };
}

const isUpperTooth = (tooth) => {
  const n = Number(tooth);
  return (n >= 11 && n <= 28) || (n >= 51 && n <= 68);
};

/** X column for status grids: deciduous share permanent columns; all shifted to status center bar. */
const statusToothX = (tooth) => {
  const n = Number(tooth);
  const mapped = DECIDUOUS_TO_PERMANENT[n] || n;
  const pos = toothChartCenters[mapped];
  if (!pos) return null;
  return pos[0] + STATUS_GRID_X_OFFSET;
};

const teethHasSelection = (csv) => parseTeethList(csv).length > 0;

const MedidentVsCcForm = ({ initialData, onSave }) => {
  const { t } = useTranslation('common');
  const [formData, setFormData] = useState(() => ({
    ...medidentVsCcDefaultFormData,
    ...normalizeTeethListFields(initialData || {}),
  }));

  useEffect(() => {
    setFormData({
      ...medidentVsCcDefaultFormData,
      ...normalizeTeethListFields(initialData || {}),
    });
  }, [initialData]);

  const debouncedSave = useRef(
    debounce((data) => {
      if (typeof onSave === 'function') onSave(data);
    }, 500)
  ).current;

  const handleChange = (field, value) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      debouncedSave(next);
      return next;
    });
  };

  const isFieldEmpty = (field) => {
    const value = formData[field];
    if (typeof value === 'boolean') return !value;
    return value == null || String(value).trim() === '';
  };

  const isSectionComplete = ({ sectionFields = [], requireAtLeastOne = false, isOptional = false }) => {
    if (!sectionFields.length) return false;
    if (requireAtLeastOne || isOptional) return sectionFields.some((field) => !isFieldEmpty(field));
    return sectionFields.every((field) => !isFieldEmpty(field));
  };

  const renderAccordionSection = ({ title, children, sectionFields = [], isOptional = false, requireAtLeastOne = false }) => {
    const hasEmptyMandatory = !isOptional && (
      requireAtLeastOne
        ? !sectionFields.some((field) => !isFieldEmpty(field))
        : sectionFields.some((field) => isFieldEmpty(field))
    );
    const showFilled = isSectionComplete({ sectionFields, requireAtLeastOne, isOptional });
    return (
      <Disclosure>
        {({ open }) => (
          <div className={`mb-4 rounded-lg ${open ? 'bg-white shadow-md' : ''}`}>
            <Disclosure.Button className="flex w-full justify-between rounded-lg px-4 py-2 text-left text-sm font-medium bg-white text-gray-900 shadow-sm hover:bg-gray-50 focus:outline-none focus-visible:ring focus-visible:ring-blue-500 focus-visible:ring-opacity-50">
              <span>{title}</span>
              <div className="flex items-center gap-2">
                {!isOptional && hasEmptyMandatory ? (
                  <span className="px-2 py-1 text-xs font-medium bg-red-50 text-red-600 rounded">{t('insurance.labels.required')}</span>
                ) : showFilled ? (
                  <span className="px-2 py-1 text-xs font-medium bg-green-50 text-green-600 rounded">{t('insurance.labels.filled')}</span>
                ) : null}
                <ChevronUpIcon className={`${open ? 'rotate-180 transform' : ''} h-5 w-5 text-gray-500 ml-2`} />
              </div>
            </Disclosure.Button>
            <Disclosure.Panel className="px-4 pt-4 pb-2 text-sm text-gray-600">{children}</Disclosure.Panel>
          </div>
        )}
      </Disclosure>
    );
  };

  const renderField = (field, label, type = 'text', required = false) => (
    <div className="flex flex-col space-y-1">
      {label && (
        <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
          {label}
          {required && <span className="text-red-500">*</span>}
        </label>
      )}
      {type === 'textarea' ? (
        <textarea
          value={formData[field] || ''}
          onChange={(e) => handleChange(field, e.target.value)}
          rows={4}
          className="rounded-md shadow-sm border-gray-300 focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50 mt-1 block w-full"
        />
      ) : type === 'date' ? (
        <EuropeanDateInput value={formData[field] || ''} onChange={(value) => handleChange(field, value)} required={required} />
      ) : (
        <input
          type={type}
          value={formData[field] || ''}
          onChange={(e) => handleChange(field, e.target.value)}
          className="rounded-md shadow-sm border-gray-300 focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
        />
      )}
    </div>
  );

  const renderOuiNon = (field, label, required = false) => (
    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
      <span className="text-sm font-medium text-gray-700 flex items-center gap-2">
        {label}
        {required && <span className="text-red-500">*</span>}
      </span>
      <div className="flex items-center gap-4">
        {['oui', 'non'].map((opt) => (
          <label key={opt} className="inline-flex items-center gap-1 text-sm cursor-pointer">
            <input
              type="radio"
              name={field}
              checked={formData[field] === opt}
              onChange={() => handleChange(field, opt)}
              className="h-4 w-4 border-gray-300 text-[rgb(141,212,209)] focus:ring-[rgb(141,212,209)]"
            />
            {opt}
          </label>
        ))}
      </div>
    </div>
  );

  const scale = (doc) => {
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    return { sx: pageWidth / MEDIDENT_IMG_W, sy: pageHeight / MEDIDENT_IMG_H, pageWidth, pageHeight };
  };

  const drawMarkX = (doc, ix, iy, fontSize = 10) => {
    const { sx, sy } = scale(doc);
    doc.setFontSize(fontSize);
    doc.setTextColor(0, 0, 0);
    doc.text('X', ix * sx, iy * sy, { align: 'center', baseline: 'middle' });
  };

  /**
   * Place wrapped text on fixed bar baselines. If content exceeds bar count,
   * split into 2 or 3 columns (same approach as MedidentNe / MedidentVsSs).
   */
  const drawMultilineIntoBars = (doc, text, barYs, x, maxWidth, fontSize = 8, { gapImg = 16 } = {}) => {
    if (!text) return;
    const { sx, sy } = scale(doc);
    doc.setFontSize(fontSize);
    doc.setTextColor(0, 0, 0);

    const maxRows = barYs.length;
    const gap = gapImg * sx;
    const fullWidth = maxWidth * sx;
    const colWidthFor = (cols) => (fullWidth - gap * (cols - 1)) / cols;
    const linesIfCols = (cols) => doc.splitTextToSize(String(text), colWidthFor(cols));
    const maxLinesIfCols = (cols) => Math.ceil(linesIfCols(cols).length / cols);

    let cols = 1;
    if (maxLinesIfCols(1) > maxRows) {
      cols = maxLinesIfCols(2) <= maxRows ? 2 : 3;
    }

    const colWidth = colWidthFor(cols);
    const lines = linesIfCols(cols);
    const perCol = Math.ceil(lines.length / cols);

    for (let c = 0; c < cols; c++) {
      const colLines = lines.slice(c * perCol, (c + 1) * perCol);
      const colX = x * sx + c * (colWidth + gap);
      colLines.forEach((line, i) => {
        if (i < maxRows) doc.text(line, colX, barYs[i] * sy);
      });
    }
  };

  const handleGeneratePDF = async () => {
    if (typeof onSave === 'function') onSave(formData);

    const doc = new jsPDF({ unit: 'px', format: 'a4' });
    const getImageBase64 = (src) =>
      new Promise((resolve) => {
        const img = new window.Image();
        img.crossOrigin = 'Anonymous';
        img.onload = function () {
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          canvas.getContext('2d').drawImage(img, 0, 0);
          resolve(canvas.toDataURL('image/png'));
        };
        img.src = src;
      });

    const [bg1, bg2] = await Promise.all([getImageBase64(medidentVsCcBg1), getImageBase64(medidentVsCcBg2)]);

    let signatureDataUrl = null;
    let signatureType = 'JPEG';
    try {
      const profile = await apiClient.get('/api/profile');
      if (profile?.signature_image) {
        signatureDataUrl = profile.signature_image;
        signatureType = signatureDataUrl.startsWith('data:image/png') ? 'PNG' : 'JPEG';
      }
    } catch (e) {
      // ignore
    }

    const { sx, sy, pageWidth, pageHeight } = scale(doc);
    doc.addImage(bg1, 'PNG', 0, 0, pageWidth, pageHeight);
    doc.setFontSize(8);
    doc.setTextColor(0, 0, 0);

    const textFieldsPage1 = [
      ['no-assure', 410, 406],
      ['nom-prenom', 410, 450],
      ['date-naissance', 410, 560],
      ['telephone', 410, 669],
      ['date-1ere-consultation', 440, 727],
      ['motif-consultation', 440, 771],
    ];
    textFieldsPage1.forEach(([key, ix, iy]) => {
      if (formData[key]) doc.text(String(formData[key]), ix * sx, iy * sy);
    });

    drawMultilineIntoBars(doc, formData.adresse, [483, 515], 410, 500, 8);
    drawMultilineIntoBars(doc, formData['medecin-dentiste'], [603, 636], 410, 500, 8);

    if (formData.hygiene && hygieneMarks[formData.hygiene]) {
      const [hx, hy] = hygieneMarks[formData.hygiene];
      drawMarkX(doc, hx, hy, 9);
    }

    if (String(formData.fumeur).toLowerCase() === 'oui') drawMarkX(doc, 480.05, 837.0, 9);
    else drawMarkX(doc, 658.55, 837.0, 9);
    if (String(formData['cas-lamal-opas']).toLowerCase() === 'oui') drawMarkX(doc, 987.1, 876.0, 9);
    else drawMarkX(doc, 987.1, 902.35, 9);
    if (String(formData['cas-accident']).toLowerCase() === 'oui') drawMarkX(doc, 987.2, 960.7, 9);
    else drawMarkX(doc, 987.1, 987.15, 9);
    if (String(formData['prestations-complementaires']).toLowerCase() === 'oui') drawMarkX(doc, 987.1, 1045.5, 9);
    else drawMarkX(doc, 987.15, 1071.85, 9);

    const chartTeeth = parseTeethList(formData['missing-chart-teeth'] || formData['missing-teeth']);
    chartTeeth.forEach((tooth) => {
      const pos = toothChartCenters[tooth];
      if (pos) drawMarkX(doc, pos[0], pos[1], 8);
    });

    Object.entries(statusOuiNon).forEach(([field, cfg]) => {
      const isOui = teethHasSelection(formData[field]);
      drawMarkX(doc, isOui ? cfg.oui[0] : cfg.non[0], isOui ? cfg.oui[1] : cfg.non[1], 8);
      // Same spacing as odontogram, shifted so 11|21 straddle the status center bar;
      // deciduous (>50) overlay permanent columns (55→15 … 75→35).
      doc.setFontSize(7);
      doc.setTextColor(0, 0, 0);
      parseTeethList(formData[field]).forEach((tooth) => {
        const x = statusToothX(tooth);
        if (x == null) return;
        const y = isUpperTooth(tooth) ? cfg.upperY : cfg.lowerY;
        doc.text(String(tooth), x * sx, y * sy, { align: 'center', baseline: 'middle' });
      });
    });

    drawMultilineIntoBars(doc, formData['prosthetic-description'], [1531, 1564, 1597], 160, 900, 8);

    // Page 2
    doc.addPage();
    doc.addImage(bg2, 'PNG', 0, 0, pageWidth, pageHeight);
    doc.setFontSize(8);
    doc.setTextColor(0, 0, 0);

    drawMultilineIntoBars(doc, formData['mesures-diagnostiques'], [139, 172, 205], 135, 920, 8);
    drawMultilineIntoBars(doc, formData['mesures-therapeutiques'], [278, 310, 343], 135, 920, 8);
    drawMultilineIntoBars(
      doc,
      formData['soins-definitifs'],
      [480, 512, 545, 577, 610, 642, 674],
      135,
      920,
      8
    );

    // Mask devis zone: keep section title « Devis », cover instruction lines + tables
    const devisMaskY = 760;
    const devisMaskH = 522;
    doc.setFillColor(255, 255, 255);
    doc.rect(120 * sx, devisMaskY * sy, 1010 * sx, devisMaskH * sy, 'F');
    doc.setFontSize(14);
    doc.setTextColor(0, 0, 0);
    doc.text(t('medidentVsCc.fields.attachedQuoteLabel'), (120 + 1010 / 2) * sx, (devisMaskY + devisMaskH / 2) * sy, {
      align: 'center',
      baseline: 'middle',
    });

    if (formData['lieu-date']) {
      doc.setFontSize(8);
      doc.text(String(formData['lieu-date']), 135 * sx, 1350 * sy);
    }

    if (signatureDataUrl) {
      try {
        doc.addImage(signatureDataUrl, signatureType, 660 * sx, 1340 * sy, 180 * sx, 60 * sy);
      } catch (e) {
        // ignore
      }
    }

    Object.entries(annexMarks).forEach(([key, [ax, ay]]) => {
      if (formData[key]) drawMarkX(doc, ax, ay, 9);
    });
    if (formData['annexe-autre-texte']) {
      doc.setFontSize(8);
      doc.text(String(formData['annexe-autre-texte']), 260 * sx, 1586 * sy);
    }

    doc.save('medident-valais-cc-form.pdf');
  };

  const ADMIN_FIELDS = ['no-assure', 'nom-prenom', 'adresse', 'date-naissance', 'medecin-dentiste', 'telephone'];
  const CONTEXT_FIELDS = [
    'date-1ere-consultation',
    'motif-consultation',
    'hygiene',
    'fumeur',
    'cas-lamal-opas',
    'cas-accident',
    'prestations-complementaires',
  ];
  const TEETH_FIELDS = [
    'missing-chart-teeth',
    'missing-teeth',
    'defective-teeth',
    'repaired-teeth',
    'periodontal-teeth',
    'prosthetic-description',
  ];
  const TREATMENT_FIELDS = ['mesures-diagnostiques', 'mesures-therapeutiques', 'soins-definitifs', 'lieu-date'];

  return (
    <form className="space-y-6" onSubmit={(e) => e.preventDefault()}>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold">{t('consultation.steps.medidentVsCc')}</h2>
        <button
          type="button"
          onClick={handleGeneratePDF}
          style={{
            backgroundColor: colors.primary.selected,
            color: 'white',
            borderRadius: '0.375rem',
            padding: '0.5rem 1rem',
            fontWeight: 500,
            border: 'none',
          }}
        >
          {t('insurance.buttons.generatePdf', 'Generate PDF')}
        </button>
      </div>

      {renderAccordionSection({
        title: t('medidentVsCc.sections.administrative'),
        sectionFields: ADMIN_FIELDS,
        children: (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {renderField('no-assure', t('medidentVsCc.fields.noAssure'))}
            {renderField('nom-prenom', t('medidentVsCc.fields.nomPrenom'), 'text', true)}
            {renderField('adresse', t('medidentVsCc.fields.adresse'), 'textarea', true)}
            {renderField('date-naissance', t('medidentVsCc.fields.dateNaissance'), 'date', true)}
            {renderField('medecin-dentiste', t('medidentVsCc.fields.medecinDentiste'), 'textarea', true)}
            {renderField('telephone', t('medidentVsCc.fields.telephone'))}
          </div>
        ),
      })}

      {renderAccordionSection({
        title: t('medidentVsCc.sections.context'),
        sectionFields: CONTEXT_FIELDS,
        children: (
          <div className="space-y-4">
            {renderField('date-1ere-consultation', t('medidentVsCc.fields.date1ereConsultation'), 'date')}
            {renderField('motif-consultation', t('medidentVsCc.fields.motifConsultation'))}
            <div className="flex flex-col space-y-1">
              <label className="text-sm font-medium text-gray-700">{t('medidentVsCc.fields.hygiene')}</label>
              <select
                value={formData.hygiene || ''}
                onChange={(e) => handleChange('hygiene', e.target.value)}
                className="rounded-md border-gray-300 shadow-sm"
              >
                <option value="">—</option>
                {HYGIENE_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>{t(`medidentVsCc.hygiene.${opt}`)}</option>
                ))}
              </select>
            </div>
            {renderOuiNon('fumeur', t('medidentVsCc.fields.fumeur'))}
            {renderOuiNon('cas-lamal-opas', t('medidentVsCc.fields.casLamalOpas'), true)}
            {renderOuiNon('cas-accident', t('medidentVsCc.fields.casAccident'), true)}
            {renderOuiNon('prestations-complementaires', t('medidentVsCc.fields.prestationsComplementaires'), true)}
          </div>
        ),
      })}

      {renderAccordionSection({
        title: t('medidentVsCc.sections.teeth'),
        sectionFields: TEETH_FIELDS,
        isOptional: true,
        children: (
          <div className="space-y-6">
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">{t('medidentVsCc.fields.missingChartTeeth')}</p>
              <TeethSelector
                selectedTeeth={formData['missing-chart-teeth'] || ''}
                onTeethSelect={(value) => handleChange('missing-chart-teeth', value)}
              />
            </div>
            {[
              ['missing-teeth', 'missingTeeth'],
              ['defective-teeth', 'defectiveTeeth'],
              ['repaired-teeth', 'repairedTeeth'],
              ['periodontal-teeth', 'periodontalTeeth'],
            ].map(([field, labelKey]) => (
              <div key={field}>
                <p className="text-sm font-medium text-gray-700 mb-2">{t(`medidentVsCc.fields.${labelKey}`)}</p>
                <TeethSelector
                  selectedTeeth={formData[field] || ''}
                  onTeethSelect={(value) => handleChange(field, value)}
                />
              </div>
            ))}
            {renderField('prosthetic-description', t('medidentVsCc.fields.prostheticDescription'), 'textarea')}
          </div>
        ),
      })}

      {renderAccordionSection({
        title: t('medidentVsCc.sections.treatment'),
        sectionFields: TREATMENT_FIELDS,
        isOptional: true,
        children: (
          <div className="space-y-4">
            {renderField('mesures-diagnostiques', t('medidentVsCc.fields.mesuresDiagnostiques'), 'textarea')}
            {renderField('mesures-therapeutiques', t('medidentVsCc.fields.mesuresTherapeutiques'), 'textarea')}
            {renderField('soins-definitifs', t('medidentVsCc.fields.soinsDefinitifs'), 'textarea')}
            {renderField('lieu-date', t('medidentVsCc.fields.lieuDate'))}
            <p className="text-xs text-gray-500">{t('medidentVsCc.fields.devisMaskedHint')}</p>
          </div>
        ),
      })}

      {renderAccordionSection({
        title: t('medidentVsCc.sections.annexes'),
        sectionFields: [
          'annexe-plan-traitement',
          'annexe-devis-dentiste',
          'annexe-devis-laboratoire',
          'annexe-radiographie',
          'annexe-autre',
        ],
        isOptional: true,
        children: (
          <div className="space-y-2">
            {[
              ['annexe-plan-traitement', 'annexePlanTraitement'],
              ['annexe-devis-dentiste', 'annexeDevisDentiste'],
              ['annexe-devis-laboratoire', 'annexeDevisLaboratoire'],
              ['annexe-radiographie', 'annexeRadiographie'],
              ['annexe-autre', 'annexeAutre'],
            ].map(([key, labelKey]) => (
              <label key={key} className="inline-flex items-center gap-2 text-sm cursor-pointer mr-4">
                <input
                  type="checkbox"
                  checked={!!formData[key]}
                  onChange={(e) => handleChange(key, e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-[rgb(141,212,209)]"
                />
                {t(`medidentVsCc.fields.${labelKey}`)}
              </label>
            ))}
            {formData['annexe-autre'] && renderField('annexe-autre-texte', t('medidentVsCc.fields.annexeAutreTexte'))}
          </div>
        ),
      })}
    </form>
  );
};

export default MedidentVsCcForm;
